import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { HUBSTOPS, SUMMER_2026_SESSIONS, formatCurrency, formatDateShort, validateEmail, validatePhone } from '@/lib/utils';
import type { Student, QuoteRequest, PriceBreakdown } from '@/types';

interface FormData {
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  students: Student[];
  selectedSessions: string[];
  lateShuttle: boolean;
}

const LATE_SHUTTLE_FEE = 25; // Per week

export default function HubstopQuote() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const hubstopId = searchParams.get('hubstop');
  const selectedHubstop = HUBSTOPS.find(h => h.id === hubstopId);

  const [formData, setFormData] = useState<FormData>({
    guardianName: '',
    guardianEmail: user?.email || '',
    guardianPhone: '',
    students: [{ id: '1', firstName: '', lastName: '' }],
    selectedSessions: [],
    lateShuttle: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!selectedHubstop) {
      navigate('/');
    }
  }, [selectedHubstop, navigate]);

  if (!selectedHubstop) {
    return null;
  }

  const calculatePriceBreakdown = (): PriceBreakdown => {
    const numberOfSessions = formData.selectedSessions.length;
    const numberOfStudents = formData.students.length;
    const basePrice = selectedHubstop.pricePerWeek;
    
    // All students share the same seat - price is per seat, not per student
    const perSessionPrice = basePrice;
    const lateShuttleFee = formData.lateShuttle ? LATE_SHUTTLE_FEE * numberOfSessions : 0;
    const subtotal = perSessionPrice * numberOfSessions + lateShuttleFee;
    
    // Volume discount for 8+ weeks
    const discount = numberOfSessions >= 8 ? subtotal * 0.05 : 0;
    
    return {
      basePrice,
      perStudentPrice: basePrice / numberOfStudents,
      numberOfStudents,
      numberOfSessions,
      sessionPrice: perSessionPrice,
      lateShuttleFee: formData.lateShuttle ? lateShuttleFee : undefined,
      subtotal,
      discount: discount > 0 ? discount : undefined,
      total: subtotal - discount,
    };
  };

  const priceBreakdown = calculatePriceBreakdown();

  const addStudent = () => {
    if (formData.students.length < 6) {
      setFormData(prev => ({
        ...prev,
        students: [...prev.students, { id: String(prev.students.length + 1), firstName: '', lastName: '' }],
      }));
    }
  };

  const removeStudent = (index: number) => {
    if (formData.students.length > 1) {
      setFormData(prev => ({
        ...prev,
        students: prev.students.filter((_, i) => i !== index),
      }));
    }
  };

  const updateStudent = (index: number, field: 'firstName' | 'lastName', value: string) => {
    setFormData(prev => ({
      ...prev,
      students: prev.students.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));
  };

  const toggleSession = (sessionId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedSessions: prev.selectedSessions.includes(sessionId)
        ? prev.selectedSessions.filter(id => id !== sessionId)
        : [...prev.selectedSessions, sessionId],
    }));
  };

  const selectAllSessions = () => {
    setFormData(prev => ({
      ...prev,
      selectedSessions: SUMMER_2026_SESSIONS.map(s => s.id),
    }));
  };

  const clearSessions = () => {
    setFormData(prev => ({ ...prev, selectedSessions: [] }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.guardianName.trim()) {
      newErrors.guardianName = 'Name is required';
    }

    if (!validateEmail(formData.guardianEmail)) {
      newErrors.guardianEmail = 'Valid email is required';
    }

    if (!validatePhone(formData.guardianPhone)) {
      newErrors.guardianPhone = 'Valid phone number is required';
    }

    const incompleteStudents = formData.students.some(s => !s.firstName.trim() || !s.lastName.trim());
    if (incompleteStudents) {
      newErrors.students = 'All students must have first and last names';
    }

    if (formData.selectedSessions.length === 0) {
      newErrors.sessions = 'Select at least one week';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    if (!user) {
      toast.error('Please sign in to submit a quote');
      return;
    }

    setIsSubmitting(true);

    try {
      const quoteRequest: Omit<QuoteRequest, 'id'> = {
        userId: user.uid,
        type: 'hubstop',
        status: 'pending',
        guardianName: formData.guardianName,
        guardianEmail: formData.guardianEmail,
        guardianPhone: formData.guardianPhone,
        pickupAddress: selectedHubstop.name,
        pickupZipcode: '',
        dropoffAddress: 'Summerside Camps at SCH',
        dropoffZipcode: '',
        hubstopId: selectedHubstop.id,
        hubstopName: selectedHubstop.name,
        students: formData.students,
        selectedSessions: formData.selectedSessions,
        lateShuttle: formData.lateShuttle,
        calculatedPrice: priceBreakdown.total,
        priceBreakdown,
        routeName: `${selectedHubstop.name} → Summerside Camps at SCH`,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Save to Firestore
      await addDoc(collection(db, 'quoteRequests'), {
        ...quoteRequest,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Quote Request Submitted!</h2>
            <p className="text-gray-600 mb-6">
              We've received your request for the <strong>{selectedHubstop.name}</strong> hubstop.
              Check your email at <strong>{formData.guardianEmail}</strong> for your detailed quote.
            </p>
            <div className="bg-shuttlebee-yellow/10 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                <strong>Total:</strong> {formatCurrency(priceBreakdown.total)} for {priceBreakdown.numberOfSessions} weeks
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/dashboard')}>View Dashboard</Button>
              <Button variant="outline" onClick={() => navigate('/')}>Back to Home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {selectedHubstop.name} Hubstop
          </h1>
          <p className="text-gray-600">
            Reserve your spot on the {selectedHubstop.name} → Summerside Camps at SCH shuttle
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Hubstop Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 bg-shuttlebee-yellow rounded-full flex items-center justify-center text-sm font-bold">
                      📍
                    </span>
                    Route Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-900">{selectedHubstop.name}</p>
                        <p className="text-sm text-gray-600">→ Summerside Camps at SCH</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-shuttlebee-gold">
                          {formatCurrency(selectedHubstop.pricePerWeek)}
                        </p>
                        <p className="text-sm text-gray-500">per week per camper, roundtrip</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Guardian Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Parent/Guardian Information</CardTitle>
                  <CardDescription>Primary contact for this registration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    label="Full Name"
                    value={formData.guardianName}
                    onChange={(e) => setFormData(prev => ({ ...prev, guardianName: e.target.value }))}
                    error={errors.guardianName}
                    placeholder="John Smith"
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      label="Email"
                      type="email"
                      value={formData.guardianEmail}
                      onChange={(e) => setFormData(prev => ({ ...prev, guardianEmail: e.target.value }))}
                      error={errors.guardianEmail}
                      placeholder="john@example.com"
                    />
                    <Input
                      label="Phone"
                      type="tel"
                      value={formData.guardianPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, guardianPhone: e.target.value }))}
                      error={errors.guardianPhone}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Students */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>Students</CardTitle>
                      <CardDescription>Add up to 6 students from the same family</CardDescription>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addStudent}
                      disabled={formData.students.length >= 6}
                    >
                      + Add Student
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {errors.students && (
                    <p className="text-sm text-red-600">{errors.students}</p>
                  )}
                  {formData.students.map((student, index) => (
                    <div key={student.id} className="flex gap-3 items-end">
                      <div className="flex-1">
                        <Input
                          label={index === 0 ? 'First Name' : undefined}
                          value={student.firstName}
                          onChange={(e) => updateStudent(index, 'firstName', e.target.value)}
                          placeholder="First name"
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          label={index === 0 ? 'Last Name' : undefined}
                          value={student.lastName}
                          onChange={(e) => updateStudent(index, 'lastName', e.target.value)}
                          placeholder="Last name"
                        />
                      </div>
                      {formData.students.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStudent(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Week Selection */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>Select Weeks</CardTitle>
                      <CardDescription>Summer 2026 • 10 weeks available</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={selectAllSessions}>
                        Select All
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={clearSessions}>
                        Clear
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {errors.sessions && (
                    <p className="text-sm text-red-600 mb-3">{errors.sessions}</p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {SUMMER_2026_SESSIONS.map((session) => {
                      const isSelected = formData.selectedSessions.includes(session.id);
                      return (
                        <button
                          key={session.id}
                          type="button"
                          onClick={() => toggleSession(session.id)}
                          className={`
                            p-3 rounded-lg border-2 text-center transition-all
                            ${isSelected
                              ? 'border-shuttlebee-yellow bg-shuttlebee-yellow/10'
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                        >
                          <p className="font-semibold text-sm">{session.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatDateShort(session.startDate)}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Options */}
              <Card>
                <CardHeader>
                  <CardTitle>Additional Options</CardTitle>
                </CardHeader>
                <CardContent>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.lateShuttle}
                      onChange={(e) => setFormData(prev => ({ ...prev, lateShuttle: e.target.checked }))}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-shuttlebee-yellow focus:ring-shuttlebee-yellow"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Late Shuttle (+{formatCurrency(LATE_SHUTTLE_FEE)}/week)</p>
                      <p className="text-sm text-gray-500">
                        Stay until 5:30 PM instead of the regular 3:30 PM pickup
                      </p>
                    </div>
                  </label>
                </CardContent>
              </Card>

              <Button
                type="submit"
                size="xl"
                variant="shuttlebee"
                className="w-full"
                disabled={isSubmitting || formData.selectedSessions.length === 0}
                loading={isSubmitting}
              >
                Submit Quote Request
              </Button>
            </form>
          </div>

          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="border-2 border-shuttlebee-yellow">
                <CardHeader className="bg-shuttlebee-yellow/10">
                  <CardTitle className="text-center">Quote Summary</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {/* Route */}
                  <div>
                    <p className="text-sm text-gray-500">Route</p>
                    <p className="font-medium">{selectedHubstop.name} → Summerside Camps at SCH</p>
                  </div>

                  {/* Weeks */}
                  <div>
                    <p className="text-sm text-gray-500">Weeks Selected</p>
                    <p className="font-medium">
                      {priceBreakdown.numberOfSessions} of 10 weeks
                    </p>
                  </div>

                  {/* Students */}
                  <div>
                    <p className="text-sm text-gray-500">Students</p>
                    <p className="font-medium">{priceBreakdown.numberOfStudents}</p>
                  </div>

                  <hr />

                  {/* Price Breakdown */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {formatCurrency(priceBreakdown.sessionPrice)} × {priceBreakdown.numberOfSessions} weeks
                      </span>
                      <span>{formatCurrency(priceBreakdown.sessionPrice * priceBreakdown.numberOfSessions)}</span>
                    </div>
                    
                    {priceBreakdown.lateShuttleFee && priceBreakdown.lateShuttleFee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Late shuttle fee</span>
                        <span>{formatCurrency(priceBreakdown.lateShuttleFee)}</span>
                      </div>
                    )}

                    {priceBreakdown.discount && priceBreakdown.discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>8+ week discount (5%)</span>
                        <span>-{formatCurrency(priceBreakdown.discount)}</span>
                      </div>
                    )}
                  </div>

                  <hr />

                  {/* Total */}
                  <div className="flex justify-between items-end">
                    <span className="font-semibold text-gray-900">Total</span>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-shuttlebee-gold">
                        {formatCurrency(priceBreakdown.total)}
                      </p>
                      {priceBreakdown.numberOfSessions > 0 && (
                        <p className="text-xs text-gray-500">
                          {formatCurrency(priceBreakdown.total / priceBreakdown.numberOfSessions)}/week per camper, roundtrip
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Note */}
                  <p className="text-xs text-gray-500 text-center pt-2">
                    All students from the same family share one seat at the same price
                  </p>
                </CardContent>
              </Card>

              {/* Back Button */}
              <Button
                type="button"
                variant="ghost"
                className="w-full mt-4"
                onClick={() => navigate('/')}
              >
                ← Back to Hubstops
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
